// Der Speicher der aktiven UI-Sprache — bewusst OHNE React und ohne api-Import.
//
// Warum getrennt von index.tsx: es gibt Stellen, die einen Text brauchen und
// keine Komponente sind (Toast-Helfer, Formatierer, ui/labels.ts). Die dürfen
// weder einen Hook aufrufen noch sich die Sprache selbst merken — sonst gäbe es
// zwei Wahrheiten, die auseinanderlaufen. Sie lesen `getLang()`/`katalog()`
// aus DIESER Datei; der React-Teil (index.tsx) hängt nur einen Abonnenten dran
// und schreibt über `setLangLokal`. Es gibt also genau EINE Variable, die weiß,
// welche Sprache gerade gilt: `aktiv`.
import { de, type Katalog } from "./de";
import { en } from "./en";

export type Lang = "de" | "en";
export type { Katalog };

/** Voreinstellung. Deutsch ist nicht „erstmal deutsch", sondern die Sprache des
 *  Hauses: alle Bestandsnutzer und alle 99 Playwright-Walks arbeiten mit den
 *  deutschen Beschriftungen. */
export const DEFAULT_LANG: Lang = "de";

/** Nur der Browser-Cache der Wahl. Die Wahrheit steht am Nutzer
 *  (`user_settings.payload.language`, GET/PUT /settings) — der localStorage
 *  sorgt lediglich dafür, dass die Oberfläche schon im ERSTEN Bild richtig
 *  steht und nicht sichtbar von Deutsch nach Englisch umspringt. */
const SPEICHER_KEY = "sb_lang";

const KATALOGE: Record<Lang, Katalog> = { de, en };

export function istLang(v: unknown): v is Lang {
  return v === "de" || v === "en";
}

function ausSpeicher(): Lang {
  try {
    const v = localStorage.getItem(SPEICHER_KEY);
    return istLang(v) ? v : DEFAULT_LANG;
  } catch {
    // localStorage kann gesperrt sein (Privat-Modus, Richtlinie). Eine
    // Einstellung darf die App nicht am Starten hindern.
    return DEFAULT_LANG;
  }
}

let aktiv: Lang = ausSpeicher();

const hoerer = new Set<() => void>();

/** Aktive Sprache. Für Nicht-Komponenten-Kontexte; in Komponenten `useT()`. */
export function getLang(): Lang {
  return aktiv;
}

/** Der getypte Katalog einer Sprache (ohne Argument: der aktiven). */
export function katalog(lang: Lang = aktiv): Katalog {
  return KATALOGE[lang];
}

/** Abonnement für React (`useSyncExternalStore`). Gibt die Abmeldung zurück. */
export function subscribeLang(cb: () => void): () => void {
  hoerer.add(cb);
  return () => {
    hoerer.delete(cb);
  };
}

/** `lang` ans Dokument schreiben. Betrifft Vorlese-Programme und Silbentrennung
 *  — NICHT die Datums-/Zahlformate: die kommen aus ui/germanDate.ts und bleiben
 *  deutsch (docs/i18n.md §Nicht-Ziele). */
export function spracheAnsHtml(lang: Lang = aktiv): void {
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}

/** Sprache umschalten — sofort, ohne Reload und ohne Server. Die Persistenz
 *  hängt am Provider (`useLang().setLang`), damit diese Datei importfrei bleibt. */
export function setLangLokal(lang: Lang): void {
  if (lang === aktiv) return;
  aktiv = lang;
  try {
    localStorage.setItem(SPEICHER_KEY, lang);
  } catch {
    /* siehe ausSpeicher(): nicht schreibbar ist kein Grund zu scheitern. */
  }
  spracheAnsHtml(lang);
  for (const cb of hoerer) cb();
}
